import type { JSONContent } from "@tiptap/core";
import {
  CONDITIONAL_INLINE_ATTR,
  CONDITIONAL_NODE_NAME,
  CONDITIONAL_TEMPLATE_ATTR,
  CONTEXT_ATTR,
  CONTEXT_NODE_NAME,
  DATA_KEY_ATTR,
  DATA_KEY_NODE_NAME,
  DID_ATTR,
  DID_NODE_NAME,
  HANDLEBARS_INLINE_ATTR,
  HANDLEBARS_NODE_NAME,
  HANDLEBARS_TEMPLATE_ATTR,
} from "src/tiptap/components/tiptap-node/dynamic-nodes/constants";
import type { DynamicContentResolution } from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-context";
import { parseMarkdownToTiptapContent } from "src/tiptap/components/tiptap-node/dynamic-nodes/render-markdown";
import {
  buildDynamicNodeResolver,
  collectReferencedKeys,
} from "src/tiptap/components/tiptap-node/dynamic-nodes/resolution";
import { shouldRetainEmptyVersionComparisonTableParagraph } from "./table-version-comparison";

export type VersionComparisonDynamicContentResolver = (params: {
  dataKeys: string[];
  latestTiptapVersion: number;
  version: number | null;
}) => Promise<DynamicContentResolution | null>;

type DynamicReferences = {
  contextKeys: string[];
  dataKeys: string[];
  hasDynamicNodes: boolean;
  requestedDataKeys: string[];
};

type DynamicReplacement = {
  content: JSONContent[];
  isBlock: boolean;
  omitEmptyParent?: boolean;
};

const getStringAttribute = (node: JSONContent, name: string) =>
  typeof node.attrs?.[name] === "string" ? node.attrs[name] : "";

const getDynamicReferences = (content: JSONContent): DynamicReferences => {
  const contextKeys = new Set<string>();
  const dataKeys = new Set<string>();
  const requestedDataKeys = new Set<string>();
  let hasDynamicNodes = false;

  const visit = (node: JSONContent) => {
    if (node.type === DATA_KEY_NODE_NAME) {
      hasDynamicNodes = true;
      const dataKey = getStringAttribute(node, DATA_KEY_ATTR);

      if (dataKey) {
        dataKeys.add(dataKey);
        requestedDataKeys.add(dataKey);
      }
    } else if (node.type === CONTEXT_NODE_NAME) {
      hasDynamicNodes = true;
      const contextKey = getStringAttribute(node, CONTEXT_ATTR);

      if (contextKey) {
        contextKeys.add(contextKey);
      }
    } else if (node.type === DID_NODE_NAME) {
      hasDynamicNodes = true;
      const did = getStringAttribute(node, DID_ATTR);

      if (did) {
        contextKeys.add(did);
      }
    } else if (
      node.type === CONDITIONAL_NODE_NAME ||
      node.type === HANDLEBARS_NODE_NAME
    ) {
      hasDynamicNodes = true;
      const template = getStringAttribute(
        node,
        node.type === CONDITIONAL_NODE_NAME
          ? CONDITIONAL_TEMPLATE_ATTR
          : HANDLEBARS_TEMPLATE_ATTR
      );

      collectReferencedKeys(template).forEach((key) =>
        requestedDataKeys.add(key)
      );
    }

    node.content?.forEach(visit);
  };

  visit(content);
  return {
    contextKeys: [...contextKeys].sort(),
    dataKeys: [...dataKeys].sort(),
    hasDynamicNodes,
    requestedDataKeys: [...requestedDataKeys].sort(),
  };
};

const hasRequiredDynamicContent = ({
  dynamicContent,
  references,
}: {
  dynamicContent: DynamicContentResolution;
  references: DynamicReferences;
}) =>
  references.dataKeys.every((dataKey) =>
    Object.prototype.hasOwnProperty.call(dynamicContent.dataKeys, dataKey)
  ) &&
  references.contextKeys.every((contextKey) =>
    Object.prototype.hasOwnProperty.call(dynamicContent.context, contextKey)
  );

const getContextValue = (
  contextKey: string,
  dynamicContent: DynamicContentResolution
) => {
  const value = dynamicContent.context[contextKey];

  return value === null || value === undefined || typeof value === "object"
    ? null
    : String(value);
};

const getDynamicValue = ({
  dynamicContent,
  node,
}: {
  dynamicContent: DynamicContentResolution;
  node: JSONContent;
}) => {
  if (node.type === DATA_KEY_NODE_NAME) {
    const resolution =
      dynamicContent.dataKeys[getStringAttribute(node, DATA_KEY_ATTR)];

    return resolution && !resolution.isUnresolved ? resolution.value : null;
  }

  if (node.type === CONTEXT_NODE_NAME || node.type === DID_NODE_NAME) {
    return getContextValue(
      getStringAttribute(
        node,
        node.type === DID_NODE_NAME ? DID_ATTR : CONTEXT_ATTR
      ),
      dynamicContent
    );
  }

  if (
    node.type === CONDITIONAL_NODE_NAME ||
    node.type === HANDLEBARS_NODE_NAME
  ) {
    const resolver = buildDynamicNodeResolver(dynamicContent);
    return resolver.evaluateHandlebars(
      getStringAttribute(
        node,
        node.type === CONDITIONAL_NODE_NAME
          ? CONDITIONAL_TEMPLATE_ATTR
          : HANDLEBARS_TEMPLATE_ATTR
      )
    );
  }

  return undefined;
};

const getDynamicReplacement = ({
  dynamicContent,
  node,
}: {
  dynamicContent: DynamicContentResolution;
  node: JSONContent;
}): DynamicReplacement | null => {
  const isDynamicNode =
    node.type === DATA_KEY_NODE_NAME ||
    node.type === CONTEXT_NODE_NAME ||
    node.type === DID_NODE_NAME ||
    node.type === CONDITIONAL_NODE_NAME ||
    node.type === HANDLEBARS_NODE_NAME;

  if (!isDynamicNode) {
    return null;
  }

  const dynamicValue = getDynamicValue({ dynamicContent, node });

  if (dynamicValue === undefined) {
    return null;
  }

  if (dynamicValue === null) {
    return { content: [], isBlock: false };
  }

  if (node.type === CONDITIONAL_NODE_NAME && dynamicValue.trim().length === 0) {
    return { content: [], isBlock: false, omitEmptyParent: true };
  }

  const isPlainTextContext =
    node.type === CONTEXT_NODE_NAME || node.type === DID_NODE_NAME;
  const parsed = isPlainTextContext
    ? null
    : parseMarkdownToTiptapContent(dynamicValue);
  const isExplicitlyBlock =
    (node.type === CONDITIONAL_NODE_NAME &&
      node.attrs?.[CONDITIONAL_INLINE_ATTR] === false) ||
    (node.type === HANDLEBARS_NODE_NAME &&
      node.attrs?.[HANDLEBARS_INLINE_ATTR] === false);
  const isBlock = Boolean(parsed?.isBlock || isExplicitlyBlock);
  const content = structuredClone(
    parsed
      ? isBlock
        ? parsed.content
        : parsed.inlineContent
      : dynamicValue
        ? [{ type: "text", text: dynamicValue }]
        : []
  );

  if (isBlock || !node.marks?.length) {
    return { content, isBlock };
  }

  return {
    content: content.map((child) => ({
      ...child,
      marks: [...(child.marks ?? []), ...node.marks!],
    })),
    isBlock,
  };
};

const replaceDynamicNodesWithResolvedContent = (
  content: JSONContent,
  dynamicContent: DynamicContentResolution
) => {
  const visit = (node: JSONContent, parentType?: string): JSONContent[] => {
    const replacement = getDynamicReplacement({
      dynamicContent,
      node,
    });

    if (replacement) {
      return replacement.content;
    }

    if (!node.content) {
      return [node];
    }

    if (node.type !== "heading" && node.type !== "paragraph") {
      const resolvedContent = node.content.flatMap((child) =>
        visit(child, node.type)
      );

      if (resolvedContent.length === 0) {
        return [];
      }

      return [{ ...node, content: resolvedContent }];
    }

    const blocks: JSONContent[] = [];
    let inline: JSONContent[] = [];
    let shouldOmitEmptyParent = false;
    const flushInlineContent = () => {
      if (inline.length) {
        blocks.push({ ...node, content: inline });
        inline = [];
      }
    };

    node.content.forEach((child) => {
      const childReplacement = getDynamicReplacement({
        dynamicContent,
        node: child,
      });

      if (!childReplacement) {
        inline.push(...visit(child, node.type));
      } else if (childReplacement.isBlock) {
        flushInlineContent();
        blocks.push(...childReplacement.content);
      } else {
        inline.push(...childReplacement.content);
      }

      shouldOmitEmptyParent ||= Boolean(
        childReplacement?.omitEmptyParent &&
          childReplacement.content.length === 0
      );
    });
    flushInlineContent();

    if (
      shouldOmitEmptyParent &&
      blocks.length === 0 &&
      !shouldRetainEmptyVersionComparisonTableParagraph(parentType)
    ) {
      return [];
    }

    return blocks.length ? blocks : [{ ...node, content: inline }];
  };

  return visit(content)[0] ?? { type: "doc", content: [] };
};

export const resolveDynamicVersionComparisonContent = async ({
  content,
  latestTiptapVersion,
  resolveDynamicContentForVersion,
  version,
}: {
  content: JSONContent;
  latestTiptapVersion: number;
  resolveDynamicContentForVersion: VersionComparisonDynamicContentResolver;
  version: number | null;
}) => {
  const references = getDynamicReferences(content);

  if (!references.hasDynamicNodes) {
    return {
      content,
      hasDynamicNodes: false,
    };
  }

  const dynamicContent = await resolveDynamicContentForVersion({
    dataKeys: references.requestedDataKeys,
    latestTiptapVersion,
    version,
  });

  if (
    !dynamicContent ||
    !hasRequiredDynamicContent({ dynamicContent, references })
  ) {
    return null;
  }

  return {
    content: replaceDynamicNodesWithResolvedContent(content, dynamicContent),
    hasDynamicNodes: true,
  };
};
